import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { History, Loader2 } from 'lucide-react'
import cardStyles from '../styles/documentCard.module.css'
import { formatChangeLogCreatedAt } from '@/lib/utils/formatChangeLogCreatedAt'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { ServiceAgreementEditForm } from '../components/ServiceAgreementEditForm'
import { ChangeReasonModal } from '../components/ChangeReasonModal'
import {
  getServiceAgreementById,
  patchServiceAgreementDraft,
  saveServiceAgreement,
} from '../services/documentManagementService'
import type {
  ServiceAgreementDocument,
  ServiceAgreementSubmitPayload,
} from '../types/document'
import { asTrimmedString } from '../utils/coerceStrings'

type PendingSave = {
  payload: ServiceAgreementSubmitPayload
  id: number
}

type EditorDirtyFlags = {
  content: boolean
  eorp: boolean
}

type DraftFormSnapshot = {
  content: string
  eorp_section: string
}

function formatHistoryTimestamp(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'number') {
    return formatChangeLogCreatedAt(value, 'MM/dd/yy hh:mm:ss a')
  }
  const raw = asTrimmedString(value)
  if (!raw) return ''
  return formatChangeLogCreatedAt(raw, 'MM/dd/yy hh:mm:ss a')
}

export function ServiceAgreementEditPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const numericId = documentId ? Number(documentId) : NaN
  const [loading, setLoading] = useState(true)
  const [doc, setDoc] = useState<ServiceAgreementDocument | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [draftSaving, setDraftSaving] = useState(false)
  const [reasonOpen, setReasonOpen] = useState(false)
  const [editorDirty, setEditorDirty] = useState<EditorDirtyFlags>({ content: false, eorp: false })
  const pendingSaveRef = useRef<PendingSave | null>(null)
  const draftSnapshotRef = useRef<DraftFormSnapshot>({ content: '', eorp_section: '' })
  const editorDirtyRef = useRef(editorDirty)
  const draftInFlightRef = useRef(false)
  editorDirtyRef.current = editorDirty

  const load = useCallback(async () => {
    if (!Number.isFinite(numericId) || numericId <= 0) return
    setLoading(true)
    try {
      const d = await getServiceAgreementById(numericId)
      const content = asTrimmedString(d.content_draft ?? d.content)
      const eorp = asTrimmedString(d.eorp_section_draft ?? d.eorp_section)
      setDoc(d)
      draftSnapshotRef.current = { content, eorp_section: eorp }
      setEditorDirty({ content: false, eorp: false })
    } catch (error) {
      showApiError(error)
      navigate('/gdpr-consent/gdpr-document', { replace: true })
    } finally {
      setLoading(false)
    }
  }, [numericId, navigate])

  useEffect(() => {
    if (!Number.isFinite(numericId) || numericId <= 0) {
      navigate('/gdpr-consent/gdpr-document', { replace: true })
      return
    }
    void load()
  }, [numericId, navigate, load])

  const updateDraftSnapshot = useCallback((patch: Partial<DraftFormSnapshot>) => {
    draftSnapshotRef.current = { ...draftSnapshotRef.current, ...patch }
  }, [])

  const flushDraftsFromRefs = useCallback(async () => {
    if (!numericId || draftInFlightRef.current) return
    const snapshot = draftSnapshotRef.current
    const dirty = editorDirtyRef.current
    const payload: Record<string, string> = {}
    if (dirty.content) payload.content_draft = snapshot.content
    if (dirty.eorp) payload.eorp_section_draft = snapshot.eorp_section
    if (Object.keys(payload).length === 0) return

    draftInFlightRef.current = true
    setDraftSaving(true)
    try {
      await patchServiceAgreementDraft(numericId, payload)
      setDoc((prev) => (prev ? { ...prev, ...payload } : prev))
      setEditorDirty({ content: false, eorp: false })
      showSuccess('Document Draft Saved Successfully')
    } catch (error) {
      showApiError(error)
    } finally {
      draftInFlightRef.current = false
      setDraftSaving(false)
    }
  }, [numericId])

  useEffect(() => {
    if (!numericId) return
    const timer = window.setInterval(() => {
      void flushDraftsFromRefs()
    }, 180_000)
    return () => window.clearInterval(timer)
  }, [numericId, flushDraftsFromRefs])

  const discardDraft = async (type: 'content' | 'eorp_section') => {
    if (!numericId || draftInFlightRef.current) return
    draftInFlightRef.current = true
    setDraftSaving(true)
    try {
      if (type === 'content') {
        await patchServiceAgreementDraft(numericId, { content_draft: '' })
        const published = asTrimmedString(doc?.content)
        updateDraftSnapshot({ content: published })
        draftSnapshotRef.current.content = published
        setDoc((prev) => (prev ? { ...prev, content_draft: null } : prev))
        setEditorDirty((x) => ({ ...x, content: false }))
      } else {
        await patchServiceAgreementDraft(numericId, { eorp_section_draft: '' })
        const published = asTrimmedString(doc?.eorp_section)
        updateDraftSnapshot({ eorp_section: published })
        draftSnapshotRef.current.eorp_section = published
        setDoc((prev) => (prev ? { ...prev, eorp_section_draft: null } : prev))
        setEditorDirty((x) => ({ ...x, eorp: false }))
      }
      showSuccess('Document Draft Discarded Successfully')
    } catch (error) {
      showApiError(error)
    } finally {
      draftInFlightRef.current = false
      setDraftSaving(false)
    }
  }

  const performSave = async (payload: ServiceAgreementSubmitPayload, id: number) => {
    setSubmitting(true)
    try {
      await saveServiceAgreement(payload, id)
      showSuccess('Document Record Updated')
      setReasonOpen(false)
      pendingSaveRef.current = null
      navigate('/gdpr-consent/gdpr-document')
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFormSubmit = async (
    payload: ServiceAgreementSubmitPayload,
    id: number | undefined,
    isDirtyEdit: boolean,
  ) => {
    if (!id) return
    if (isDirtyEdit) {
      pendingSaveRef.current = { payload, id }
      setReasonOpen(true)
      return
    }
    await performSave(payload, id)
  }

  const handleReasonSubmit = async (reason: string) => {
    const pending = pendingSaveRef.current
    if (!pending) return
    await performSave({ ...pending.payload, change_reason: reason }, pending.id)
  }

  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-[#f4f7f6]">
        <Loader2 className="h-10 w-10 animate-spin text-[#1597ff]" aria-label="Loading" />
      </div>
    )
  }

  const hasContentDraft = Boolean(doc?.content_draft)
  const hasEorpDraft = Boolean(doc?.eorp_section_draft)

  return (
    <div className="min-h-screen w-full bg-[#f4f7f6] pb-12 font-sans text-slate-900">
      <PageHeader
        title="EDIT SERVICE AGREEMENT"
        testIdRoot="service-agreement-edit-page-header"
      />
      <div className="mx-auto flex w-full flex-col gap-6 px-3 py-6 sm:px-6">
        <div className="w-full min-w-0 overflow-visible rounded-xl border border-slate-200/90 bg-white shadow-[0_5px_24px_-8px_rgba(15,23,42,0.12)] ring-1 ring-slate-100">
          <ServiceAgreementEditForm
            document={doc}
            submitting={submitting && !reasonOpen}
            draftSaving={draftSaving}
            editorDirty={editorDirty}
            hasContentDraft={hasContentDraft}
            hasEorpDraft={hasEorpDraft}
            onContentChange={(value) => {
              updateDraftSnapshot({ content: value })
              setEditorDirty((d) => ({ ...d, content: true }))
            }}
            onEorpChange={(value) => {
              updateDraftSnapshot({ eorp_section: value })
              setEditorDirty((d) => ({ ...d, eorp: true }))
            }}
            onSaveDraftContent={() => void flushDraftsFromRefs()}
            onSaveDraftEorp={() => void flushDraftsFromRefs()}
            onDiscardDraftContent={() => void discardDraft('content')}
            onDiscardDraftEorp={() => void discardDraft('eorp_section')}
            onSubmit={handleFormSubmit}
            onCancel={() => navigate('/gdpr-consent/gdpr-document')}
          />

          {doc?.notes && doc.notes.length > 0 ? (
            <div className="p-4 sm:p-6 border-t border-slate-200">
              <div className={cardStyles.card}>
                <div className={cardStyles.cardHeader}>
                  <History className={cardStyles.cardIcon} />
                  <h3 className={cardStyles.cardTitle}>History Log</h3>
                </div>
                <div className={cardStyles.cardBody}>
                  <div className="rounded-md border border-slate-200 bg-slate-50/80">
                    <ul className="divide-y divide-slate-200">
                      {doc.notes.map((row, idx) => (
                        <li
                          key={`${row.created_at ?? ''}-${idx}`}
                          className="flex flex-col gap-2 p-4 sm:flex-row"
                        >
                          <div className="shrink-0 border-slate-200 sm:w-48 sm:border-r sm:pr-4">
                            <p className="text-sm font-semibold text-slate-800">{row.name ?? '—'}</p>
                            <p className="text-xs text-slate-500">
                              {formatHistoryTimestamp(row.created_at)}
                            </p>
                          </div>
                          <div className="min-w-0 flex-1 text-sm text-slate-700">
                            {asTrimmedString(doc.version)} - {asTrimmedString(row.change_reason)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ChangeReasonModal
        isOpen={reasonOpen}
        submitting={submitting}
        onClose={() => {
          setReasonOpen(false)
          pendingSaveRef.current = null
        }}
        onSubmit={(reason) => void handleReasonSubmit(reason)}
      />
    </div>
  )
}
